import { useSyncExternalStore } from "react";

// Minimal History-API router — no dependency on react-router, matching this
// app's react/react-dom-only footprint. Both the static server (web-server.ts)
// and the Vite dev server fall back to index.html for unknown paths, so real
// URL paths work without a hash.

const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): string {
  return window.location.pathname + window.location.search;
}

// Subscribe a component to the current location (pathname + parsed query).
// Re-renders on back/forward and on navigate() below.
export function useLocation(): { pathname: string; query: URLSearchParams } {
  const href = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [pathname, search = ""] = href.split("?");
  return { pathname, query: new URLSearchParams(search) };
}

// ── Navigation blockers ────────────────────────────────────────────────────
// A screen with unsaved work (e.g. the DevHub page editor) registers a blocker
// so EVERY in-app navigation — programmatic navigate() and browser Back/Forward
// (popstate) — is confirmed first, not just the one button that happened to call
// its own dialog. `fn` returns true to allow, false to cancel; it may answer
// asynchronously (the app's confirm() resolves a promise). Returns an unregister.
type NavBlocker = () => boolean | Promise<boolean>;
const blockers = new Set<NavBlocker>();

export function registerNavBlocker(fn: NavBlocker): () => void {
  blockers.add(fn);
  return () => {
    blockers.delete(fn);
  };
}

// Ask every registered blocker whether a navigation may proceed. Resolves to a
// plain boolean synchronously when every blocker answers synchronously (the
// common case — including "no blockers registered"), otherwise to a promise. Any
// single false cancels the navigation.
function mayNavigate(): boolean | Promise<boolean> {
  if (blockers.size === 0) return true;
  const answers = [...blockers].map((b) => b());
  if (answers.every((a): a is boolean => typeof a === "boolean")) {
    return answers.every(Boolean);
  }
  return Promise.all(answers).then((all) => all.every(Boolean));
}

// The location the router last committed to. Used to undo a blocked browser
// Back/Forward by re-pushing it while the confirm dialog is up.
let committed = typeof window !== "undefined" ? getSnapshot() : "";

function commit(to: string, opts?: { replace?: boolean }): void {
  if (opts?.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  committed = getSnapshot();
  emit();
}

// Programmatic navigation. pushState/replaceState don't fire popstate, so we
// emit() ourselves to notify subscribers. A registered nav blocker (unsaved
// editor) can veto or defer the commit.
//
// Returns the same confirm/cancel outcome the blocker produced — true/false
// synchronously, or a Promise<boolean> when a blocker answers asynchronously
// (the app's confirm() dialog) — so a caller that optimistically updates its
// own UI ahead of the URL (e.g. useDevhubCatalog's `open()`) can await the
// SAME decision instead of re-consulting the blockers itself (which would pop
// the confirm dialog a second time).
export function navigate(to: string, opts?: { replace?: boolean }): boolean | Promise<boolean> {
  if (to === getSnapshot()) return true; // no-op: same URL, avoid history spam
  const allowed = mayNavigate();
  if (allowed === true) {
    commit(to, opts);
    return true;
  }
  if (allowed === false) return false; // a blocker declined synchronously
  return allowed.then((ok) => {
    if (ok) commit(to, opts);
    return ok;
  });
}

// Browser Back/Forward. By the time popstate fires the URL has already changed,
// so a declining blocker is honoured by re-pushing the committed URL (the
// standard "undo the pop" pattern); an async blocker holds the URL there until it
// resolves, then advances to the target only if allowed.
function onPopState(): void {
  const target = getSnapshot();
  const allowed = mayNavigate();
  if (allowed === true) {
    committed = target;
    emit();
    return;
  }
  const from = committed;
  window.history.pushState(null, "", from); // undo the pop while (or before) we decide
  if (allowed === false) return;
  void allowed.then((ok) => {
    if (!ok) return; // declined: URL already restored to `from`
    committed = target;
    window.history.pushState(null, "", target);
    emit();
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", onPopState);
}
